import React from 'react';
import { When } from 'react-if';

import classes from '@nerdwallet/base-styles/classes';
import { Box } from '@nerdwallet/currency/components/Box';
import { Button } from '@nerdwallet/currency/components/Button';
import { Link } from '@nerdwallet/currency/components/Link';
import { Stack } from '@nerdwallet/currency/components/Stack';
import { Title } from '@nerdwallet/currency/components/Title';
import { Text } from '@nerdwallet/currency/components/Text';
import Print from '@nerdwallet/react-icon/nerdwallet-ui/Print';

import IconTextLink from '@components/icon-text-link';
import BetaTag from '@components/beta-tag/beta-tag';

import styles from './simple-page-header.module.scss';

const PrintButton = () => {
  const handlePrintClick = () => {
    if (typeof window === 'undefined') {
      return;
    }
    window.print();
  };

  return (
    <Link className={styles.printContainer} onClick={handlePrintClick}>
      <div className={styles.printWrapper}>
        <Print size={16} />
        <p className={styles.printText}>Print</p>
      </div>
    </Link>
  );
};

type CtaLinkProps = {
  href: string;
  icon: React.FC;
  iconSize: number;
  label?: string;
  onClick?: () => void;
};

const CtaLink = ({
  href,
  icon,
  iconSize,
  label,
  onClick,
}: CtaLinkProps): React.ReactNode => (
  <IconTextLink
    href={href}
    icon={icon}
    iconSize={iconSize}
    label={label}
    onClick={onClick}
    small
  />
);

type Props = {
  backLinkIcon?: React.FC;
  backLinkLabel?: string;
  backLinkOnClick?: () => void;
  backLinkTo?: string;

  betaTooltipContent?: string;

  ctaButtonLabel?: string;
  ctaButtonOnClick?: () => void;
  ctaButtonDisabled?: boolean;

  ctaLinkIcon?: React.FC;
  ctaLinkLabel?: string;
  ctaLinkOnClick?: () => void;
  ctaLinkTo?: string;

  description?: string | React.ReactNode;

  isBackLinkVisible?: boolean;
  isBetaVisible?: boolean;
  isCtaButtonVisible?: boolean;
  isCtaLinkVisible?: boolean;
  isPrintButtonVisible?: boolean;

  subtitle?: string | React.ReactNode;
  title?: string | React.ReactNode;
  inSituComponent?: React.ReactNode;
};

const SimplePageHeader = ({
  backLinkIcon,
  backLinkLabel,
  backLinkTo,
  betaTooltipContent,
  ctaButtonLabel,
  ctaButtonOnClick,
  ctaButtonDisabled,
  ctaLinkIcon,
  ctaLinkLabel,
  ctaLinkOnClick,
  ctaLinkTo,
  description,
  isBackLinkVisible,
  isBetaVisible,
  isCtaButtonVisible,
  isCtaLinkVisible,
  isPrintButtonVisible,
  subtitle,
  title,
  inSituComponent,
}: Props): React.ReactNode => (
  <Stack spacing={2}>
    <Box display="flex" justifyContent="space-between">
      <When condition={isBackLinkVisible}>
        <div>
          <IconTextLink
            icon={backLinkIcon}
            iconSize={16}
            label={backLinkLabel}
            href={backLinkTo}
          />
        </div>
      </When>

      <When condition={isCtaLinkVisible}>
        {/* The CTA link appears at the top in mobile web. */}
        <div className={styles.ctaLinkNarrow}>
          <CtaLink icon={ctaLinkIcon} iconSize={24} href={ctaLinkTo} />
        </div>
      </When>
    </Box>

    <Box display="flex" justifyContent="space-between" alignItems="flex-end">
      <div className={classes({ 'width-100': !!isPrintButtonVisible })}>
        <Stack spacing={2}>
          <When condition={!!inSituComponent}>{inSituComponent}</When>
          <div className={styles.title}>
            <Title>{title}</Title>
            <When condition={isBetaVisible}>
              <div className={classes('margin-left-2')}>
                <BetaTag tooltipContent={betaTooltipContent} />
              </div>
            </When>
            <When condition={isPrintButtonVisible}>
              <PrintButton />
            </When>
          </div>
          <When condition={!!subtitle}>
            <Title size="small">{subtitle}</Title>
          </When>
          <When condition={!!description}>
            <Text>{description}</Text>
          </When>
        </Stack>

        <When condition={isCtaButtonVisible}>
          <Box marginTop={4}>
            <Button
              primary
              onClick={ctaButtonOnClick}
              disabled={ctaButtonDisabled}
            >
              {ctaButtonLabel}
            </Button>
          </Box>
        </When>
      </div>

      <When condition={isCtaLinkVisible}>
        {/* The CTA link appears at the bottom in desktop. */}
        <div className={styles.ctaLinkWide}>
          <CtaLink
            href={ctaLinkTo}
            icon={ctaLinkIcon}
            iconSize={24}
            label={ctaLinkLabel}
            onClick={ctaLinkOnClick}
          />
        </div>
      </When>
    </Box>
  </Stack>
);

export default SimplePageHeader;
